import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | slow | error
  const [name, setName] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const user = await refresh();
          setName(user?.name || "");
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        /* keep trying */
      }
      if (tries.current >= 8) {
        setState("slow");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="min-h-screen velvet bg-purple-deep flex items-center justify-center px-6">
      <div className="relative z-10 max-w-lg w-full text-center">
        {state === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full border-2 border-gold/30 border-t-gold mx-auto mb-8 animate-spin" />
            <h1 className="font-heading text-white text-4xl mb-3">Confirming your payment…</h1>
            <p className="text-white/70">One moment — we're opening the doors.</p>
          </>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center mx-auto mb-8 shadow-[0_0_50px_rgba(212,175,55,0.5)]">
              <Check className="w-10 h-10 text-purple-deep" strokeWidth={2.5} />
            </div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-4">Welcome aboard</p>
            <h1 className="font-heading text-white text-[clamp(2rem,5vw,3rem)] mb-4">
              You're in{name ? `, ${name}` : ""}.
            </h1>
            <p className="text-white/80 text-lg mb-8 max-w-md mx-auto">
              Your payment went through and your account now has full access to How Truth Travels — eight modules, taken at your own pace.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-8 py-4 hover:bg-gold-pale transition-colors"
                data-testid="success-enter-btn"
              >
                Enter the course <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="https://www.skool.com/oxytocin-house-of-learning/about"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold rounded-full px-6 py-4 hover:bg-white/10 transition-colors"
              >
                <Users className="w-4 h-4" /> Say hello in the community
              </a>
            </div>
          </motion.div>
        )}

        {(state === "slow" || state === "error") && (
          <>
            <h1 className="font-heading text-white text-4xl mb-3">{state === "slow" ? "Nearly there." : "Something interrupted us."}</h1>
            <p className="text-white/75 mb-8 max-w-md mx-auto">
              {state === "slow"
                ? "Your payment is still confirming. This can take a moment — head to your course and it will unlock shortly."
                : "We couldn't confirm this payment. If you were charged, contact edward@oxytocin-learning.com and we'll put it right."}
            </p>
            <Link to={state === "slow" ? "/dashboard" : "/join"} className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-8 py-4 hover:bg-gold-pale transition-colors">
              {state === "slow" ? "Go to my course" : "Back to enrolment"} <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
